import type {
  ConversationPriority,
  ConversationStatus,
  CustomerSentiment,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { Badge } from "@redotech/redo-web/components/ui/badge";
import { DataTableColumnHeader } from "@redotech/redo-web/components/ui/data-table";
import { formatTimeAgo } from "@redotech/util/date-format";
import type { ColumnDef } from "@tanstack/react-table";
import { memo } from "react";
import type { ConversationColumnMeta } from "../types";

/**
 * Status badge component
 */
const StatusBadge = memo(function StatusBadge({
  status,
}: {
  status: ConversationStatus;
}) {
  const config = getStatusConfig(status);
  return (
    <Badge className={config.className} variant="outline">
      {config.label}
    </Badge>
  );
});

function getStatusConfig(status: ConversationStatus): {
  label: string;
  className: string;
} {
  switch (status) {
    case "OPEN":
      return { label: "Open", className: "border-blue-500 text-blue-700" };
    case "IN_PROGRESS":
      return {
        label: "In Progress",
        className: "border-yellow-500 text-yellow-700",
      };
    case "SNOOZED":
      return {
        label: "Snoozed",
        className: "border-purple-500 text-purple-700",
      };
    case "CLOSED":
      return { label: "Closed", className: "border-gray-500 text-gray-600" };
    case "ARCHIVED":
      return { label: "Archived", className: "border-gray-400 text-gray-500" };
    case "HIDDEN":
      return { label: "Hidden", className: "border-gray-300 text-gray-400" };
    case "DELETED":
      return { label: "Deleted", className: "border-red-500 text-red-700" };
    default:
      return { label: status, className: "" };
  }
}

/**
 * Priority badge component
 */
const PriorityBadge = memo(function PriorityBadge({
  priority,
}: {
  priority: ConversationPriority;
}) {
  const config = getPriorityConfig(priority);
  return (
    <Badge className={config.className} variant="outline">
      {config.label}
    </Badge>
  );
});

function getPriorityConfig(priority: ConversationPriority): {
  label: string;
  className: string;
} {
  switch (priority) {
    case "CRITICAL":
      return { label: "Critical", className: "border-red-600 text-red-700" };
    case "HIGH":
      return { label: "High", className: "border-orange-500 text-orange-700" };
    case "MEDIUM":
      return {
        label: "Medium",
        className: "border-yellow-500 text-yellow-700",
      };
    case "LOW":
      return { label: "Low", className: "border-gray-400 text-gray-600" };
    default:
      return { label: priority, className: "" };
  }
}

/**
 * Sentiment badge component
 */
const SentimentBadge = memo(function SentimentBadge({
  sentiment,
}: {
  sentiment: CustomerSentiment;
}) {
  const config = getSentimentConfig(sentiment);
  return (
    <Badge className={config.className} variant="outline">
      {config.label}
    </Badge>
  );
});

function getSentimentConfig(sentiment: CustomerSentiment): {
  label: string;
  className: string;
} {
  switch (sentiment) {
    case "POSITIVE":
      return {
        label: "Positive",
        className: "border-green-500 text-green-700",
      };
    case "NEUTRAL":
      return { label: "Neutral", className: "border-gray-400 text-gray-600" };
    case "NEGATIVE":
      return { label: "Negative", className: "border-red-500 text-red-700" };
    default:
      return { label: sentiment, className: "" };
  }
}

/**
 * Get column definitions for conversations table
 */
export function getConversationsTableColumns(): ColumnDef<
  ConversationListItemResponse,
  unknown
>[] {
  return [
    {
      id: "status",
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
      meta: { label: "Status", variant: "status" } as ConversationColumnMeta,
      enableSorting: true,
      enableColumnFilter: true,
      size: 120,
    },
    {
      id: "summary",
      accessorKey: "summary",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Summary" />
      ),
      cell: ({ row }) => {
        const summary = row.original.summary;
        return (
          <div className="max-w-md truncate text-sm">
            {summary ?? (
              <span className="text-muted-foreground">No summary</span>
            )}
          </div>
        );
      },
      meta: { label: "Summary", variant: "text" } as ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 300,
    },
    {
      id: "priority",
      accessorKey: "priority",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Priority" />
      ),
      cell: ({ row }) => {
        const priority = row.original.priority;
        if (!priority) {
          return <span className="text-muted-foreground">-</span>;
        }
        return <PriorityBadge priority={priority} />;
      },
      meta: { label: "Priority", variant: "badge" } as ConversationColumnMeta,
      enableSorting: true,
      enableColumnFilter: true,
      size: 100,
    },
    {
      id: "customerSentiment",
      accessorKey: "customerSentiment",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Sentiment" />
      ),
      cell: ({ row }) => {
        const sentiment = row.original.customerSentiment;
        if (!sentiment) {
          return <span className="text-muted-foreground">-</span>;
        }
        return <SentimentBadge sentiment={sentiment} />;
      },
      meta: { label: "Sentiment", variant: "badge" } as ConversationColumnMeta,
      enableSorting: true,
      enableColumnFilter: true,
      size: 100,
    },
    {
      id: "lastCustomerMessageAt",
      accessorKey: "lastCustomerMessageAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Last Activity" />
      ),
      cell: ({ row }) => {
        const date = row.original.lastCustomerMessageAt;
        if (!date) {
          return <span className="text-muted-foreground">-</span>;
        }
        return (
          <span className="text-sm text-muted-foreground">
            {formatTimeAgo(date.toISOString())}
          </span>
        );
      },
      meta: {
        label: "Last Activity",
        variant: "date",
      } as ConversationColumnMeta,
      enableSorting: true,
      enableColumnFilter: false,
      size: 140,
    },
    {
      id: "readAt",
      accessorKey: "readAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Read" />
      ),
      cell: ({ row }) => {
        const readAt = row.original.readAt;
        return (
          <div className="flex items-center justify-center">
            {readAt ? (
              <span className="text-muted-foreground">Read</span>
            ) : (
              <Badge variant="default">Unread</Badge>
            )}
          </div>
        );
      },
      meta: { label: "Read", variant: "badge" } as ConversationColumnMeta,
      enableSorting: true,
      enableColumnFilter: true,
      size: 80,
    },
  ];
}
